import { SiteRagReport, RagStatus, Recommendation, RiskItem, TimeSeriesData } from "../types/siteRag";

const generateTimeSeries = (avgTemp: number): TimeSeriesData[] => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months.map((month, i) => {
    const variation = Math.sin((i / 11) * Math.PI) * 15;
    return {
      month,
      avgTemp: avgTemp + variation,
      peakTemp: avgTemp + variation + 10,
      wetBulbTemp: avgTemp + variation - 5,
      coolingEfficiency: 100 - (variation > 0 ? variation * 2 : 0)
    };
  });
};

export const DEMO_SITES: SiteRagReport[] = [
  {
    id: "demo-abilene",
    siteName: "Abilene Strategic Hub",
    address: "Abilene, Texas, USA",
    location: {
      lat: 32.4487,
      lng: -99.7331,
      address: "Abilene, Texas, USA"
    },
    overallScore: 88,
    overallStatus: "green" as RagStatus,
    summary: "Abilene presents a premier opportunity for BTM data center development. Its location within the ERCOT region, combined with exceptional proximity to high-voltage transmission and a pro-development regulatory climate, makes it an ideal candidate for SMR-powered architectures.",
    createdAt: new Date().toISOString(),
    timeSeries: generateTimeSeries(64),
    categories: [
      {
        id: "grid",
        label: "Power Infrastructure",
        status: "green" as RagStatus,
        score: 92,
        weight: 0.3,
        explanation: "Excellent proximity to ERCOT transmission backbone with high available capacity.",
        metrics: [
          { id: "dist_sub", label: "Substation Distance", status: "green", score: 95, value: "1.2 miles", explanation: "Direct font-access to 345kV lines." },
          { id: "grid_cap", label: "Grid Capacity", status: "green", score: 90, value: "High", explanation: "Low congestion in this node." }
        ]
      },
      {
        id: "cooling",
        label: "Cooling Potential",
        status: "green" as RagStatus,
        score: 78,
        weight: 0.2,
        explanation: "Manageable ambient temperatures; favorable for absorption cooling systems.",
        metrics: [
          { id: "amb_temp", label: "Ambient Temp", status: "green", score: 80, value: "64°F avg", explanation: "Moderate annual average." },
          { id: "wet_bulb", label: "Wet Bulb Temp", status: "amber", score: 75, value: "72°F peak", explanation: "Occasional humidity peaks." }
        ]
      },
      {
        id: "site",
        label: "Site Logistics",
        status: "green" as RagStatus,
        score: 95,
        weight: 0.15,
        explanation: "Flat topography with industrial zoning already in place.",
        metrics: [
          { id: "land_avail", label: "Land Availability", status: "green", score: 98, value: "450 acres", explanation: "Large contiguous parcel." },
          { id: "topography", label: "Topography", status: "green", score: 92, value: "Flat", explanation: "Minimal grading required." }
        ]
      },
      {
        id: "regulatory",
        label: "Regulatory & Permitting",
        status: "green" as RagStatus,
        score: 90,
        weight: 0.2,
        explanation: "Texas offers a highly favorable environment for BTM power and SMR deployment.",
        metrics: [
          { id: "nuclear_friend", label: "Nuclear Permitting", status: "green", score: 90, value: "Favorable", explanation: "Texas pro-nuclear stance." },
          { id: "zoning", label: "Zoning Alignment", status: "green", score: 92, value: "Industrial", explanation: "Industrial zoning in place." },
          { id: "env_const", label: "Env. Constraints", status: "green", score: 85, value: "Low", explanation: "Minimal environmental constraints." }
        ]
      },
      {
        id: "logistics",
        label: "Logistics & Access",
        status: "green" as RagStatus,
        score: 88,
        weight: 0.1,
        explanation: "Excellent highway and fiber connectivity.",
        metrics: [
          { id: "road_prox", label: "Road Proximity", status: "green", score: 90, value: "Interstate", explanation: "I-20 and US-83 access." },
          { id: "port_prox", label: "Port / Airport Access", status: "green", score: 85, value: 2.5, unit: "ms", explanation: "Low latency to regional IX." }
        ]
      }
    ],
    insights: {
      powerStrategy: "SMR baseload + absorption cooling is highly recommended for this site.",
      coolingStrategy: "Utilize high-efficiency air-cooled chillers with adiabatic assist during summer peaks.",
      regulatoryOutlook: "Fast-track permitting likely due to local pro-nuclear stance.",
      commercialViability: "High ROI potential due to low land cost and direct grid interconnection savings."
    },
    executiveSummary: "Abilene presents a strong platform for a behind-the-meter SMR-powered data center campus. The three candidate parcels (total 1,285 acres) combine excellent logistics (I-40, multi-rail access), robust municipal water/wastewater headroom (~50% utilization), and a uniquely pro-nuclear state environment. The primary engineering challenge is exceptional tornado risk, which we address through hardened, below-grade or reinforced safety systems and a resilient, island-capable site microgrid.",
    sitingNotes: "Gently rolling agricultural terrain (est. 0–8% slopes), typical regional loams; proximity (10–20 mi) to Lake Graham; bottomlands warrant wetlands screening; FEMA flood mapping and field delineations required in early due diligence.",
    climateSevereWeather: {
      risks: [
        "Tornadoes: history of multiple violent F4/EF-3+ events in the Jackson area within a decade; design for tornadoes and extreme pressure differentials is mandatory.",
        "Ice storms (5–10 yr): extended grid outages possible; islandable BTM architecture + black-start are central to our design basis."
      ],
      implications: "Humidity & heat: summer wet-bulb 75–78 °F; cooling systems sized for high humidity; hybrid/dry-assist options evaluated."
    },
    infrastructureInterconnection: {
      electricGrid: "Served by TVA / Jackson Energy Authority; major transmission present; TVA queue is congested due to AI demand. BTM microgrid: deliver most power on campus at distribution voltage; the grid becomes backup/supplemental—reducing interconnection complexity and schedule risk.",
      transportFiber: "I-40 access, three-rail service (Class I + short line); heavy-haul routing feasible. Dual diverse backbone fiber paths along Memphis–Nashville corridor.",
      municipalUtilities: [
        { system: "Water (treatment)", totalCapacity: "22.4 MGD", currentUse: "14.0 MGD", available: "8.4 MGD" },
        { system: "Wastewater (treatment)", totalCapacity: "23.44 MGD", currentUse: "11.7 MGD", available: "11.74 MGD" }
      ]
    },
    developmentPathway: [
      { phase: "Phase 0", title: "Control & diligence", description: "Parcel control, surveys, wetlands/floodplain, preliminary TVA/JEA engagement, airport impact analysis, community outreach." },
      { phase: "Phase 1", title: "Pre-app & site prep", description: "NRC/TDEC/TVA pre-app, zoning/Special Exception, initial civil works, utilities to fence line." },
      { phase: "Phase 2", title: "SMR licensing & build", description: "Licensing, nuclear island construction, balance-of-plant, data hall build." }
    ],
    appendix: [
      { title: "Site-specific geographic and environmental characteristics", content: "Parcels are located approximately 10-20 miles from Lake Graham, a 500-acre Tennessee Wildlife Resources Agency reservoir constructed in 1982 with a 51-foot earthen dam. Soil characteristics typical of the region include Memphis silt loam, Loring silt loam, and alluvial soils near waterways." },
      { title: "Severe weather and climate considerations for nuclear design", content: "Madison County presents exceptionally high tornado risk. The region experienced three violent tornadoes (F4 or EF-3+) within a nine-year period. These violent events with estimated wind speeds of 166-200+ mph place extraordinary structural demands on nuclear facilities." }
    ],
    btmOptions: [
      { technology: "Solar + BESS", riskLevel: "High", impactLevel: "Red", reason: "Land size requirements are very high and would limit the data center capacity.", action: "No action is required" },
      { technology: "Wind + BESS", riskLevel: "Medium", impactLevel: "Amber", reason: "Technically feasible but limited due to site dimensions. A maximum of 2 wind turbines can be placed on site.", action: "Conduct detailed analysis on energy yield and turbine layout." },
      { technology: "Natural Gas + BoP", riskLevel: "Medium", impactLevel: "Amber", reason: "A gas pipeline needs to be built to supply the site. Permitting and construction-related issues expected.", action: "Conduct detailed analysis on the feasibility of building a gas pipeline." },
      { technology: "Nuclear SMR", riskLevel: "Low", impactLevel: "Green", reason: "High power density fits the site footprint perfectly. Provides 24/7 firm baseload.", action: "Proceed with detailed licensing and site characterization." }
    ],
    connectionRisks: [
      { category: "Transport Links", riskLevel: "Low", impactLevel: "Green", reason: "No significant transport risks. Close to major highways.", action: "No action is required." },
      { category: "Power Connections", riskLevel: "Medium", impactLevel: "Green", reason: "Proximity of local powerlines requires setback planning.", action: "Commission a blast assessment and follow setbacks." },
      { category: "Fibre Connections", riskLevel: "Low", impactLevel: "Green", reason: "Multiple Tier 1 providers adjacent to the site.", action: "Confirm redundant path routing." },
      { category: "Availability Zone", riskLevel: "Medium", impactLevel: "Green", reason: "Proximity to other data centers is limited.", action: "Review possible plots for future expansion." }
    ],
    developmentRisks: [
      { category: "Weather", riskLevel: "Low", impactLevel: "Green", reason: "No significant weather risks beyond typical regional patterns.", action: "No action is required." },
      { category: "Topographical", riskLevel: "Low", impactLevel: "Green", reason: "Desktop data suggests site is of a neutral elevation and relatively flat.", action: "Conduct a site-specific topographical assessment." },
      { category: "River Flood Risk", riskLevel: "Low", impactLevel: "Green", reason: "No significant River Flooding Risk.", action: "No action is required." },
      { category: "Surface Water Flood Risk", riskLevel: "Medium", impactLevel: "Green", reason: "Area at medium risk of surface water flooding.", action: "Conduct a site-specific flood assessment." },
      { category: "Seismic Risk", riskLevel: "Low", impactLevel: "Green", reason: "Site is in a low-risk zone for earthquakes.", action: "No further action required." }
    ],
    risks: [
      { id: "r1", category: "regulatory", label: "SMR Permitting", severity: "medium", impact: "Potential timeline delays in NRC licensing.", mitigation: "Engage with regulators early; leverage Texas state-level incentives." }
    ],
    recommendations: [
      { type: "power", title: "SMR Deployment", description: "Deploy 300MW SMR cluster for baseload.", rationale: "Matches high grid capacity and low land cost.", architecture: "SMR + Thermal Storage" },
      { type: "cooling", title: "Absorption Cooling", description: "Leverage waste heat from SMR for cooling.", rationale: "Increases overall system efficiency.", architecture: "Absorption Chillers" }
    ],
    datasets: {
      grid: { substationDist: 1.2, capacity: "High" as const, complexity: "Low" as const, voltage: "345kV" },
      climate: { avgTemp: 17.8, peakTemp: 36.7, avgWetBulb: 11.1, humidity: 55, waterStress: "Low" as const },
      land: { acres: 450, elevation: 524, slope: 1.5, floodRisk: "Low" as const },
      infrastructure: { fiberDensity: "High" as const, latency: 2.5, roadAccess: "Interstate" as const }
    }
  },
  {
    id: "demo-phoenix",
    siteName: "Phoenix Tech Corridor",
    address: "Phoenix, Arizona, USA",
    location: {
      lat: 33.4484,
      lng: -112.0740,
      address: "Phoenix, Arizona, USA"
    },
    overallScore: 62,
    overallStatus: "amber" as RagStatus,
    summary: "Phoenix offers world-class infrastructure and a mature data center ecosystem. However, extreme ambient temperatures and water scarcity present significant operational challenges that require advanced cooling architectures to maintain commercial viability.",
    createdAt: new Date().toISOString(),
    timeSeries: generateTimeSeries(75),
    categories: [
      {
        id: "grid",
        label: "Power Infrastructure",
        status: "green" as RagStatus,
        score: 88,
        weight: 0.3,
        explanation: "Robust local grid with significant data center experience.",
        metrics: [
          { id: "dist_sub", label: "Substation Distance", status: "green", score: 90, value: "0.8 miles", explanation: "Very close to existing infrastructure." },
          { id: "grid_cap", label: "Grid Capacity", status: "green", score: 85, value: "High", explanation: "Well-served industrial area." }
        ]
      },
      {
        id: "cooling",
        label: "Cooling Potential",
        status: "red" as RagStatus,
        score: 35,
        weight: 0.2,
        explanation: "Extreme ambient temperatures significantly impact cooling efficiency and PUE.",
        metrics: [
          { id: "amb_temp", label: "Ambient Temp", status: "red", score: 20, value: "75°F avg", explanation: "Frequent peaks above 110°F." },
          { id: "wet_bulb", label: "Wet Bulb Temp", status: "amber", score: 50, value: "68°F peak", explanation: "Dry heat helps, but peaks are high." }
        ]
      },
      {
        id: "site",
        label: "Site Logistics",
        status: "amber" as RagStatus,
        score: 65,
        weight: 0.15,
        explanation: "Land is available but expensive; high competition from other tech firms.",
        metrics: [
          { id: "land_avail", label: "Land Availability", status: "amber", score: 60, value: "80 acres", explanation: "Limited large parcels remaining." },
          { id: "topography", label: "Topography", status: "green", score: 90, value: "Flat", explanation: "Ideal for construction." }
        ]
      },
      {
        id: "regulatory",
        label: "Regulatory & Permitting",
        status: "green" as RagStatus,
        score: 85,
        weight: 0.2,
        explanation: "Arizona is very supportive of data center development.",
        metrics: [
          { id: "nuclear_friend", label: "Nuclear Permitting", status: "green", score: 80, value: "Favorable", explanation: "Arizona has existing nuclear (Palo Verde)." },
          { id: "zoning", label: "Zoning Alignment", status: "green", score: 90, value: "Industrial", explanation: "Industrial zones available." },
          { id: "env_const", label: "Env. Constraints", status: "green", score: 85, value: "Low", explanation: "Desert terrain, few environmental barriers." }
        ]
      },
      {
        id: "logistics",
        label: "Logistics & Access",
        status: "green" as RagStatus,
        score: 90,
        weight: 0.1,
        explanation: "World-class fiber and highway connectivity.",
        metrics: [
          { id: "road_prox", label: "Road Proximity", status: "green", score: 95, value: "Interstate", explanation: "I-10, I-17 corridor." },
          { id: "port_prox", label: "Port / Airport Access", status: "green", score: 85, value: 1.5, unit: "ms", explanation: "Very low latency to Phoenix IX." }
        ]
      }
    ],
    insights: {
      powerStrategy: "Solar-heavy grid with BESS is ideal; SMR for night-time baseload.",
      coolingStrategy: "Closed-loop dry cooling is mandatory due to extreme water stress.",
      regulatoryOutlook: "Standard permitting; strong support for data center infrastructure.",
      commercialViability: "High demand for AI compute in this region, but OpEx will be higher due to cooling."
    },
    executiveSummary: "Phoenix presents a conditional platform for a behind-the-meter SMR-powered data center campus. While the infrastructure and data center ecosystem are world-class, extreme ambient temperatures and severe water stress require a specialized cooling architecture. The site's proximity to major fiber backbones and a robust existing grid makes it a prime candidate for high-density AI workloads if cooling challenges are mitigated.",
    sitingNotes: "Flat desert terrain with minimal grading requirements; proximity to major substations; water rights are the primary siting constraint; FEMA flood mapping shows minimal risk.",
    climateSevereWeather: {
      risks: [
        "Extreme Heat: Summer peaks regularly exceed 110°F, impacting air-cooled efficiency.",
        "Dust Storms (Haboobs): Requires advanced filtration for both the SMR and data hall air intakes."
      ],
      implications: "Humidity & heat: summer wet-bulb 65–70 °F; cooling systems must be sized for extreme dry-bulb peaks; closed-loop dry cooling mandatory."
    },
    infrastructureInterconnection: {
      electricGrid: "Served by SRP; major transmission corridor; grid is stable but heavily loaded during summer peaks. BTM microgrid provides critical relief and firm baseload.",
      transportFiber: "Excellent road access; world-class fiber density with multiple Tier 1 providers. Direct connection to Phoenix IX.",
      municipalUtilities: [
        { system: "Water (treatment)", totalCapacity: "100.0 MGD", currentUse: "95.0 MGD", available: "5.0 MGD (Highly Restricted)" },
        { system: "Wastewater (treatment)", totalCapacity: "80.0 MGD", currentUse: "60.0 MGD", available: "20.0 MGD" }
      ]
    },
    developmentPathway: [
      { phase: "Phase 0", title: "Water Rights Acquisition", description: "Securing long-term water rights or greywater agreements is the critical first step." },
      { phase: "Phase 1", title: "Environmental Permitting", description: "Air quality and heat rejection impact studies required by local authorities." },
      { phase: "Phase 2", title: "Infrastructure Build-out", description: "Heavy focus on specialized cooling infrastructure and BTM microgrid integration." }
    ],
    appendix: [
      { title: "Thermal Management in Arid Climates", content: "The Phoenix site requires a move away from traditional evaporative cooling. Advanced heat-reuse absorption chilling can utilize SMR waste heat to drive cooling loops, significantly reducing water consumption." },
      { title: "Grid Resilience and Peak Shaving", content: "By co-locating SMRs, the data center can operate independently of the grid during summer peak demand periods, avoiding high demand charges and potential brownouts." }
    ],
    btmOptions: [
      { technology: "Solar + BESS", riskLevel: "Medium", impactLevel: "Amber", reason: "High irradiance, but massive land footprint required for 24/7 firming.", action: "Evaluate adjacent parcels for utility-scale solar integration." },
      { technology: "Wind + BESS", riskLevel: "High", impactLevel: "Red", reason: "Low mean power density in this specific valley location.", action: "No action required." },
      { technology: "Natural Gas + BoP", riskLevel: "Low", impactLevel: "Green", reason: "Existing high-pressure gas mains nearby. Excellent interim solution.", action: "Initiate interconnect discussions with local gas utility." },
      { technology: "Nuclear SMR", riskLevel: "Medium", impactLevel: "Amber", reason: "Water scarcity complicates heat rejection. Dry cooling required.", action: "Conduct detailed thermal discharge and cooling water analysis." }
    ],
    connectionRisks: [
      { category: "Transport Links", riskLevel: "Low", impactLevel: "Green", reason: "Excellent highway and airport access.", action: "No action is required." },
      { category: "Power Connections", riskLevel: "Low", impactLevel: "Green", reason: "Major SRP transmission corridor adjacent.", action: "Initiate preliminary grid study." },
      { category: "Fibre Connections", riskLevel: "Low", impactLevel: "Green", reason: "World-class fiber density. Direct connection to Phoenix IX.", action: "No action is required." },
      { category: "Availability Zone", riskLevel: "Low", impactLevel: "Green", reason: "Established data center hub.", action: "No action is required." }
    ],
    developmentRisks: [
      { category: "Weather", riskLevel: "High", impactLevel: "Red", reason: "Extreme heat impacts cooling efficiency and equipment life.", action: "Design for 115°F+ ambient conditions." },
      { category: "Topographical", riskLevel: "Low", impactLevel: "Green", reason: "Flat desert terrain.", action: "Standard grading plan." },
      { category: "Water Availability", riskLevel: "High", impactLevel: "Red", reason: "Severe regional water stress.", action: "Mandate zero-water cooling architecture." },
      { category: "Wildfire Risk", riskLevel: "Medium", impactLevel: "Green", reason: "Proximity to dry brush areas.", action: "Implement defensible space and site-wide hydrant system." }
    ],
    risks: [
      { id: "r2", category: "environmental", label: "Extreme Heat", severity: "high", impact: "Risk of equipment derating during summer peaks.", mitigation: "Redundant cooling systems and liquid cooling architecture." },
      { id: "r3", category: "environmental", label: "Water Scarcity", severity: "high", impact: "Limited water for evaporative cooling.", mitigation: "Closed-loop dry cooling systems." }
    ],
    recommendations: [
      { type: "cooling", title: "Liquid Cooling", description: "Implement direct-to-chip liquid cooling.", rationale: "Necessary to handle high ambient heat.", architecture: "DLC + Dry Cooling" },
      { type: "power", title: "Hybrid Power", description: "Combine grid with on-site gas peaking.", rationale: "Ensures reliability during grid stress events.", architecture: "Grid + Gas Peakers" }
    ],
    datasets: {
      grid: { substationDist: 0.8, capacity: "High" as const, complexity: "Medium" as const, voltage: "230kV" },
      climate: { avgTemp: 23.9, peakTemp: 47.8, avgWetBulb: 16.7, humidity: 30, waterStress: "High" as const },
      land: { acres: 80, elevation: 331, slope: 0.5, floodRisk: "Low" as const },
      infrastructure: { fiberDensity: "High" as const, latency: 1.5, roadAccess: "Interstate" as const }
    }
  },
  {
    id: "demo-newark",
    siteName: "Newark Urban Edge",
    address: "Newark, New Jersey, USA",
    location: {
      lat: 40.7357,
      lng: -74.1724,
      address: "Newark, New Jersey, USA"
    },
    overallScore: 38,
    overallStatus: "red" as RagStatus,
    summary: "Newark faces significant headwinds for large-scale BTM deployment. Grid congestion in the PJM region, combined with extreme land scarcity and complex urban regulatory hurdles, makes this site a high-friction candidate for near-term development.",
    createdAt: new Date().toISOString(),
    timeSeries: generateTimeSeries(54),
    categories: [
      {
        id: "grid",
        label: "Power Infrastructure",
        status: "amber" as RagStatus,
        score: 55,
        weight: 0.3,
        explanation: "Aging infrastructure with significant grid congestion in the PJM region.",
        metrics: [
          { id: "dist_sub", label: "Substation Distance", status: "amber", score: 60, value: "2.5 miles", explanation: "Requires significant line extension." },
          { id: "grid_cap", label: "Grid Capacity", status: "red", score: 40, value: "Low", explanation: "High congestion; long interconnection queue." }
        ]
      },
      {
        id: "cooling",
        label: "Cooling Potential",
        status: "amber" as RagStatus,
        score: 68,
        weight: 0.2,
        explanation: "Moderate climate but high humidity impacts wet bulb performance.",
        metrics: [
          { id: "amb_temp", label: "Ambient Temp", status: "green", score: 75, value: "54°F avg", explanation: "Favorable for free cooling in winter." },
          { id: "wet_bulb", label: "Wet Bulb Temp", status: "amber", score: 60, value: "76°F peak", explanation: "High humidity during summer." }
        ]
      },
      {
        id: "site",
        label: "Site Logistics",
        status: "red" as RagStatus,
        score: 25,
        weight: 0.15,
        explanation: "Extremely limited land; high cost and potential brownfield remediation needed.",
        metrics: [
          { id: "land_avail", label: "Land Availability", status: "red", score: 15, value: "12 acres", explanation: "Insufficient for large-scale BTM deployment." },
          { id: "topography", label: "Topography", status: "amber", score: 50, value: "Urban", explanation: "Complex site prep required." }
        ]
      },
      {
        id: "regulatory",
        label: "Regulatory & Permitting",
        status: "red" as RagStatus,
        score: 30,
        weight: 0.2,
        explanation: "Complex urban permitting and high regulatory friction for energy projects.",
        metrics: [
          { id: "nuclear_friend", label: "Nuclear Permitting", status: "red", score: 20, value: "Restrictive", explanation: "NJ urban nuclear siting extremely difficult." },
          { id: "zoning", label: "Zoning Alignment", status: "red", score: 30, value: "Mixed", explanation: "Urban mixed-use; requires variance." },
          { id: "env_const", label: "Env. Constraints", status: "amber", score: 40, value: "High", explanation: "Brownfield remediation required." }
        ]
      },
      {
        id: "logistics",
        label: "Logistics & Access",
        status: "green" as RagStatus,
        score: 88,
        weight: 0.1,
        explanation: "World-class fiber density and proximity to transatlantic cable landings.",
        metrics: [
          { id: "road_prox", label: "Road Proximity", status: "green", score: 85, value: "Interstate", explanation: "I-95, NJ Turnpike access." },
          { id: "port_prox", label: "Port / Airport Access", status: "green", score: 92, value: 0.8, unit: "ms", explanation: "Ultra-low latency to NY IX." }
        ]
      }
    ],
    insights: {
      powerStrategy: "Grid-only connection likely impossible; BTM SMR faces extreme urban permitting hurdles.",
      coolingStrategy: "Standard air-cooled systems viable but space-constrained.",
      regulatoryOutlook: "Multi-year permitting timeline expected.",
      commercialViability: "Low; CAPEX and land costs prohibitive for this use case."
    },
    executiveSummary: "Newark presents a challenging platform for a large-scale SMR-powered data center campus. The primary constraints are extreme land costs, urban density, and aging grid infrastructure. However, for specialized low-latency financial workloads, the world-class fiber density and favorable cooling climate provide significant technical advantages if the site acquisition and permitting hurdles can be cleared.",
    sitingNotes: "Urban brownfield sites; environmental remediation (Phase I/II) mandatory; limited parcel sizes require vertical data center architecture.",
    climateSevereWeather: {
      risks: [
        "Coastal Flooding: Proximity to Passaic River and Newark Bay requires elevated critical infrastructure.",
        "Winter Storms: Heavy snowfall can impact site access and outdoor equipment."
      ],
      implications: "Humidity & heat: summer wet-bulb 72–75 °F; cooling systems can utilize free cooling for 60% of the year."
    },
    infrastructureInterconnection: {
      electricGrid: "Served by PSEG; grid is highly congested; interconnection timelines can exceed 5 years for >50MW. BTM generation is one candidate path to earlier power, but it still requires PSEG interconnection and planning review and faces significant urban permitting hurdles.",
      transportFiber: "World-class fiber density; proximity to major transatlantic cable landings. Excellent rail and port access.",
      municipalUtilities: [
        { system: "Water (treatment)", totalCapacity: "80.0 MGD", currentUse: "70.0 MGD", available: "10.0 MGD" },
        { system: "Wastewater (treatment)", totalCapacity: "100.0 MGD", currentUse: "90.0 MGD", available: "10.0 MGD" }
      ]
    },
    developmentPathway: [
      { phase: "Phase 0", title: "Brownfield Remediation", description: "Environmental cleanup and site stabilization are required before any construction." },
      { phase: "Phase 1", title: "Vertical Design Approval", description: "Securing variances for multi-story data center and SMR integration in urban context." },
      { phase: "Phase 2", title: "BTM Microgrid Implementation", description: "Develop the BTM microgrid alongside PSEG interconnection and planning review; a non-export BTM design does not by itself exempt the site from interconnection review." }
    ],
    appendix: [
      { title: "Urban SMR Integration", content: "Integrating SMRs in high-density urban areas like Newark requires advanced containment and security measures. Below-grade safety systems are designed for such environments." },
      { title: "Financial Edge Computing", content: "The proximity to New York City financial markets makes Newark a prime candidate for ultra-low latency edge nodes, despite the high cost of entry." }
    ],
    btmOptions: [
      { technology: "Solar + BESS", riskLevel: "High", impactLevel: "Red", reason: "Urban density precludes meaningful solar deployment.", action: "No action required." },
      { technology: "Wind + BESS", riskLevel: "High", impactLevel: "Red", reason: "Urban environment unsuitable for wind turbines.", action: "No action required." },
      { technology: "Natural Gas + BoP", riskLevel: "Medium", impactLevel: "Amber", reason: "Gas available, but emissions regulations in urban area are strict.", action: "Conduct air quality permitting feasibility study." },
      { technology: "Nuclear SMR", riskLevel: "High", impactLevel: "Red", reason: "High population density and EPZ constraints.", action: "Conduct detailed analysis of ONR/NRC previous EPZ designations for urban sites." }
    ],
    connectionRisks: [
      { category: "Transport Links", riskLevel: "Low", impactLevel: "Green", reason: "Excellent rail, port, and highway access.", action: "No action is required." },
      { category: "Power Connections", riskLevel: "High", impactLevel: "Red", reason: "Grid is highly congested; interconnection timelines exceed 5 years.", action: "Focus entirely on BTM microgrid strategy." },
      { category: "Fibre Connections", riskLevel: "Low", impactLevel: "Green", reason: "World-class fiber density; proximity to transatlantic cables.", action: "No action is required." },
      { category: "Availability Zone", riskLevel: "Medium", impactLevel: "Amber", reason: "Space constrained for future expansion.", action: "Evaluate vertical expansion options." }
    ],
    developmentRisks: [
      { category: "Coastal Flood Risk", riskLevel: "High", impactLevel: "Amber", reason: "Proximity to Passaic River and Newark Bay.", action: "Conduct site-specific flood assessment and elevate critical infrastructure." },
      { category: "Brownfield Risk", riskLevel: "High", impactLevel: "Red", reason: "Historical industrial use guarantees contamination.", action: "Phase I/II ESA mandatory before commitment." },
      { category: "Residential Risk", riskLevel: "High", impactLevel: "Amber", reason: "Dense residential areas nearby.", action: "Acoustic attenuation and visual screening required." },
      { category: "Airport Risk", riskLevel: "Medium", impactLevel: "Amber", reason: "Proximity to Newark Liberty International (EWR).", action: "Check FAA height restrictions for cooling towers/stacks." }
    ],
    risks: [
      { id: "r4", category: "infrastructure", label: "Brownfield Risk", severity: "high", impact: "Unknown remediation costs and delays.", mitigation: "Phase I/II ESA mandatory before commitment." },
      { id: "r5", category: "regulatory", label: "Urban Permitting", severity: "high", impact: "Public opposition and complex zoning variances.", mitigation: "Extensive community engagement and lobbying." }
    ],
    recommendations: [
      { type: "site", title: "Site Relocation", description: "Seek alternative sites in less congested PJM nodes.", rationale: "Reduces interconnection risk.", architecture: "N/A" },
      { type: "power", title: "Microgrid Evaluation", description: "Evaluate multi-story data center designs with microgrid.", rationale: "Maximizes limited footprint.", architecture: "Multi-story + Microgrid" }
    ],
    datasets: {
      grid: { substationDist: 2.5, capacity: "Constrained" as const, complexity: "High" as const, voltage: "138kV" },
      climate: { avgTemp: 12.2, peakTemp: 35.0, avgWetBulb: 20.0, humidity: 70, waterStress: "Moderate" as const },
      land: { acres: 12, elevation: 6, slope: 2.0, floodRisk: "Medium" as const },
      infrastructure: { fiberDensity: "High" as const, latency: 0.8, roadAccess: "Interstate" as const }
    }
  }
];
